import React, { useState, useEffect } from 'react';
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, DollarSign, BarChart } from 'lucide-react';
import { getScriptAnalysis } from "@/lib/api";
import { ScriptAnalysis } from "@/types/analysis";
import { AnalysisLoader } from "@/components/AnalysisLoader";

const ResultsPage = () => {
  const { scriptId } = useParams() as { scriptId: string };
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState<ScriptAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState<string | null>(null);
  const [opportunityStatuses, setOpportunityStatuses] = useState<{[key: string]: 'accepted' | 'rejected' | 'pending'}>({});

  useEffect(() => {
    if (scriptId) {
      getScriptAnalysis(scriptId)
        .then(data => {
          setAnalysis(data);
          const initialStatuses: {[key: string]: 'accepted' | 'rejected' | 'pending'} = {};
          data.brand_opportunities.forEach((opp: { id: string | number; }) => {
            initialStatuses[opp.id] = 'pending';
          });
          setOpportunityStatuses(initialStatuses);
          setLoading(false);
        })
        .catch(err => {
          setError(err.message);
          setLoading(false);
        });
    }
  }, [scriptId]);

  if (loading) {
    return <AnalysisLoader />;
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (!analysis) {
    return <div>No analysis data found.</div>;
  }

  const totalValue = analysis.brand_opportunities
    .filter(opp => opportunityStatuses[opp.id] !== 'rejected')
    .reduce((sum, opp) => sum + opp.value, 0);

  const renderScriptContent = () => {
    let content = analysis.script_content;
    analysis.brand_opportunities.forEach(opp => {
      content = content.replace(opp.script_excerpt, `<span id="opp-${opp.id}" class="cursor-pointer bg-yellow-100 hover:bg-yellow-200">${opp.script_excerpt}</span>`);
    });
    return <pre className="whitespace-pre-wrap text-sm text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: content }} onClick={handleScriptClick} />;
  };

  const handleScriptClick = (e: React.MouseEvent<HTMLPreElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'SPAN' && target.id.startsWith('opp-')) {
      const oppId = target.id.replace('opp-', '');
      setSelectedOpportunity(oppId);
      const opportunityRow = document.getElementById(`row-${oppId}`);
      if (opportunityRow) {
        opportunityRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const handleOpportunityClick = (oppId: string) => {
    setSelectedOpportunity(oppId);

    const scriptElement = document.getElementById(`opp-${oppId}`);
    if (scriptElement) {
      scriptElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleStatusChange = (oppId: string, status: 'accepted' | 'rejected') => {
    setOpportunityStatuses(prev => ({ ...prev, [oppId]: status }));
  };

  return (
    <div className="min-h-screen bg-yellow-50 p-6 flex justify-center">
      <div className="w-full max-w-5xl">
        <Button variant="ghost" onClick={() => navigate('/projects/new')} className="mb-6 text-slate-900">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Create Project
        </Button>

        <header className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900">Script Analysis Report</h1>
          <p className="text-lg text-slate-600 mt-2">
            {/* Analysis for "{analysis.script.title}" */}
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white shadow-md">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-900">Total Opportunities</CardTitle>
                    <FileText className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                    <div className="text-2xl font-bold text-slate-900">{analysis.brand_opportunities.length}</div>
                    <p className="text-xs text-muted-foreground">identified in the script</p>
                </CardContent>
            </Card>
            <Card className="bg-white shadow-md">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-900">Estimated Total Value</CardTitle>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                    <div className="text-2xl font-bold text-slate-900">${totalValue.toLocaleString()}</div>
                    <p className="text-xs text-muted-foreground">based on current selections</p>
                </CardContent>
            </Card>
            <Card className="bg-white shadow-md">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-900">Confidence Level</CardTitle>
                    <BarChart className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                    <div className="text-2xl font-bold text-slate-900">High</div>
                    <p className="text-xs text-muted-foreground">overall match confidence</p>
                </CardContent>
            </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card className="bg-white shadow-md">
            <CardHeader>
              <CardTitle className="text-slate-900">Script Content</CardTitle>
            </CardHeader>
            <CardContent>
              {renderScriptContent()}
            </CardContent>
          </Card>

          <Card className="bg-white shadow-md">
            <CardHeader>
              <CardTitle className="text-slate-900">Identified Integration Opportunities</CardTitle>
              <CardDescription>The following potential brand integrations have been identified in the script.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analysis.brand_opportunities.map((opp) => (
                  <Card
                    key={opp.id}
                    id={`row-${opp.id}`}
                                         className={`transition-all duration-300 cursor-pointer ${selectedOpportunity === opp.id ? 'border-2 border-primary shadow-lg' : 'bg-white hover:bg-slate-50'}`}
                                         onClick={() => handleOpportunityClick(opp.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg font-semibold text-slate-900">{opp.keyword}</CardTitle>
                          <CardDescription className="text-sm text-slate-600">{opp.type} - {opp.confidence} Confidence</CardDescription>
                          <CardDescription className="text-sm text-slate-600">Suggested Brand: {opp.brand}</CardDescription>
                        </div>
                        <div className="text-lg font-bold text-slate-900">${opp.value.toLocaleString()}</div>
                      </div>
                      <p className="text-sm text-slate-700 mt-2">{opp.details}</p>
                      <div className="flex justify-end space-x-2 mt-4">
                        <Button
                          size="sm"
                          variant={opportunityStatuses[opp.id] === 'accepted' ? 'default' : 'outline'}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStatusChange(opp.id, 'accepted');
                          }}
                        >
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant={opportunityStatuses[opp.id] === 'rejected' ? 'destructive' : 'outline'}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStatusChange(opp.id, 'rejected');
                          }}
                        >
                          Reject
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;